/**
 * DISTRIBUTIONS PAGE - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 *
 * Features:
 * - Calculate monthly distributions based on share percentages
 * - Track opening/closing balances per partner
 * - View distribution history
 * - Savings pool allocation (remaining % split equally)
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  PiggyBank,
  Calculator,
  RefreshCw,
  Play,
  Check,
  ChevronDown,
  ChevronUp,
  Users,
  Percent,
  Building2
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import { Modal } from '../../components/ui/Modal'
import { GlassCard, ContentCard } from '../../components/ui/GlassCard'
import { Badge } from '../../components/ui/Badge'
import { ProgressBar } from '../../components/ui/ProgressBar'
import { SkeletonCard } from '../../components/ui/Skeleton'
import { apiClient } from '../../lib/api'

// Animation configuration
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig
  }
}

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springConfig
  }
}

interface Partner {
  id: number
  name: string
  email: string
  share_percentage: number
  bank_name: string | null
  bank_account: string | null
  bank_iban: string | null
  status: string
}

interface Distribution {
  id?: number
  partner_id: number
  partner_name?: string
  year: number
  month: string
  opening_balance_usd: number
  opening_balance_pkr: number
  income_usd: number
  income_pkr: number
  ending_balance_usd: number
  ending_balance_pkr: number
  notes?: string
}

interface PartnerShareData {
  partners: Partner[]
  totalShare: number
  remainingShare: number
}

interface CalculationResult {
  year: number
  month: string
  total_usd_profit: number
  total_pkr_profit: number
  distributions: {
    partner_id: number
    partner_name: string
    share_percentage: number
    usd_share: number
    pkr_share: number
  }[]
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

const PARTNER_COLORS = ['blue', 'purple', 'green', 'orange', 'pink'] as const

export function DistributionsPage() {
  const [loading, setLoading] = useState(true)
  const [partnerShares, setPartnerShares] = useState<PartnerShareData | null>(null)
  const [distributions, setDistributions] = useState<Distribution[]>([])
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  // Modal states
  const [isCalculateModalOpen, setIsCalculateModalOpen] = useState(false)
  const [calculating, setCalculating] = useState(false)
  const [calculationResult, setCalculationResult] = useState<CalculationResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Form for calculation
  const [calcMonth, setCalcMonth] = useState(MONTHS[new Date().getMonth()])
  const [calcYear, setCalcYear] = useState(new Date().getFullYear())

  // Expanded partner details
  const [expandedPartner, setExpandedPartner] = useState<number | null>(null)

  useEffect(() => {
    fetchData()
  }, [selectedYear])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [sharesResponse, distResponse] = await Promise.all([
        apiClient.get('/financials/partner-shares'),
        apiClient.get(`/financials/distributions?year=${selectedYear}`)
      ])

      if (sharesResponse.success) {
        setPartnerShares(sharesResponse.data)
      }

      if (distResponse.success) {
        setDistributions(distResponse.data || [])
      }
    } catch (err) {
      console.error('Failed to fetch data:', err)
      setError('Failed to load distribution data')
    } finally {
      setLoading(false)
    }
  }

  const handleCalculateDistributions = async () => {
    try {
      setCalculating(true)
      setError(null)
      setCalculationResult(null)

      const response = await apiClient.post('/financials/calculate-distributions', {
        year: calcYear,
        month: calcMonth
      })

      if (response.success) {
        setCalculationResult(response.data)
        await fetchData()
      }
    } catch (err: any) {
      console.error('Failed to calculate distributions:', err)
      setError(err.message || 'Failed to calculate distributions. Make sure pool balance is set for this month.')
    } finally {
      setCalculating(false)
    }
  }

  const formatCurrency = (amount: number, currency: 'USD' | 'PKR') => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(amount)
    }
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  // Group distributions by partner
  const distributionsByPartner = distributions.reduce((acc, dist) => {
    if (!acc[dist.partner_id]) {
      acc[dist.partner_id] = []
    }
    acc[dist.partner_id].push(dist)
    return acc
  }, {} as Record<number, Distribution[]>)

  // Calculate total earnings per partner for the year
  const partnerEarnings = partnerShares?.partners.map(partner => {
    const partnerDists = distributionsByPartner[partner.id] || []
    const totalUsd = partnerDists.reduce((sum, d) => sum + Number(d.income_usd || 0), 0)
    const totalPkr = partnerDists.reduce((sum, d) => sum + Number(d.income_pkr || 0), 0)
    const latestDist = partnerDists[0]

    return {
      ...partner,
      total_usd: totalUsd,
      total_pkr: totalPkr,
      current_balance_usd: latestDist?.ending_balance_usd || 0,
      current_balance_pkr: latestDist?.ending_balance_pkr || 0
    }
  }) || []

  // Loading state
  if (loading) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-52 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse" />
            <div className="h-4 w-80 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse" />
          </div>
          <div className="flex gap-3">
            <div className="h-10 w-24 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-xl animate-pulse" />
            <div className="h-10 w-44 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-xl animate-pulse" />
          </div>
        </div>
        <SkeletonCard className="h-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <SkeletonCard className="h-64" />
          <SkeletonCard className="h-64" />
          <SkeletonCard className="h-64" />
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="p-6 max-w-[1600px] mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight">
            Partner Distributions
          </h1>
          <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary mt-1">
            Calculate and track monthly profit distributions to partners
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={fetchData}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            icon={<Calculator className="w-4 h-4" />}
            onClick={() => setIsCalculateModalOpen(true)}
          >
            Calculate Distributions
          </Button>
        </div>
      </motion.div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            className="mb-6"
          >
            <GlassCard className="border border-red/20 bg-red/5">
              <div className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 rounded-xl bg-red/10 flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-red" />
                </div>
                <div className="flex-1">
                  <p className="text-red font-medium">{error}</p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setError(null)}
                >
                  Dismiss
                </Button>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Allocation Overview */}
      <motion.div variants={itemVariants}>
        <GlassCard>
          <div className="p-5">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple/10 to-pink/10 flex items-center justify-center">
                  <Percent className="w-5 h-5 text-purple" />
                </div>
                <div>
                  <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                    Share Allocation
                  </h2>
                  <p className="text-footnote text-light-text-secondary dark:text-dark-text-secondary">
                    {partnerShares?.partners.length || 0} active partners
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-caption1 text-light-text-tertiary">Allocated</p>
                  <p className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary">
                    {partnerShares?.totalShare.toFixed(1)}%
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-caption1 text-light-text-tertiary">Savings Pool</p>
                  <p className="text-title3 font-bold text-green">
                    {partnerShares?.remainingShare.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            {/* Multi-segment Progress Bar */}
            <div className="mb-4">
              <div className="h-5 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-full overflow-hidden flex">
                {partnerShares?.partners.map((partner, index) => (
                  <motion.div
                    key={partner.id}
                    initial={{ width: 0 }}
                    animate={{ width: `${partner.share_percentage}%` }}
                    transition={{ delay: 0.2 + index * 0.1, ...springConfig }}
                    className={`h-full ${
                      index % 5 === 0 ? 'bg-blue' :
                      index % 5 === 1 ? 'bg-purple' :
                      index % 5 === 2 ? 'bg-green' :
                      index % 5 === 3 ? 'bg-orange' :
                      'bg-pink'
                    }`}
                    title={`${partner.name}: ${partner.share_percentage}%`}
                  />
                ))}
                {(partnerShares?.remainingShare || 0) > 0 && (
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${partnerShares?.remainingShare}%` }}
                    transition={{ delay: 0.5, ...springConfig }}
                    className="h-full bg-gradient-to-r from-green/60 to-green"
                    title={`Savings Pool: ${partnerShares?.remainingShare}%`}
                  />
                )}
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4">
              {partnerShares?.partners.map((partner, index) => (
                <div key={partner.id} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${
                    index % 5 === 0 ? 'bg-blue' :
                    index % 5 === 1 ? 'bg-purple' :
                    index % 5 === 2 ? 'bg-green' :
                    index % 5 === 3 ? 'bg-orange' :
                    'bg-pink'
                  }`} />
                  <span className="text-footnote text-light-text-secondary dark:text-dark-text-secondary">
                    {partner.name} ({partner.share_percentage}%)
                  </span>
                </div>
              ))}
              {(partnerShares?.remainingShare || 0) > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green/60 to-green" />
                  <span className="text-footnote text-green font-medium">
                    Savings Pool ({partnerShares?.remainingShare}%)
                  </span>
                </div>
              )}
            </div>
          </div>
        </GlassCard>
      </motion.div>

      {/* Year Selector */}
      <motion.div variants={itemVariants} className="flex items-center justify-between mt-6 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue/10 to-purple/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-blue" />
          </div>
          <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
            Partner Earnings ({selectedYear})
          </h2>
        </div>
        <Select
          value={selectedYear.toString()}
          onChange={(value) => setSelectedYear(parseInt(value))}
          options={[
            { value: '2024', label: '2024' },
            { value: '2025', label: '2025' },
            { value: '2026', label: '2026' }
          ]}
        />
      </motion.div>

      {/* Partner Earnings Cards */}
      {partnerEarnings.length === 0 ? (
        <motion.div variants={itemVariants}>
          <ContentCard>
            <div className="text-center py-16">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={springConfig}
                className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple/10 to-pink/10 flex items-center justify-center"
              >
                <PiggyBank className="w-10 h-10 text-light-text-quaternary dark:text-dark-text-quaternary" />
              </motion.div>
              <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
                No Partners Found
              </h3>
              <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
                Add partners with share percentages to start tracking distributions
              </p>
            </div>
          </ContentCard>
        </motion.div>
      ) : (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          variants={containerVariants}
        >
          {partnerEarnings.map((partner, index) => (
            <motion.div
              key={partner.id}
              variants={cardVariants}
              custom={index}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              <ContentCard
                className="cursor-pointer"
                onClick={() => setExpandedPartner(expandedPartner === partner.id ? null : partner.id)}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br ${
                          index % 5 === 0 ? 'from-blue to-blue/70' :
                          index % 5 === 1 ? 'from-purple to-purple/70' :
                          index % 5 === 2 ? 'from-green to-green/70' :
                          index % 5 === 3 ? 'from-orange to-orange/70' :
                          'from-pink to-pink/70'
                        }`}
                      >
                        <span className="text-white font-bold text-body">
                          {partner.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </span>
                      </motion.div>
                      <div>
                        <h3 className="font-semibold text-body text-light-text-primary dark:text-dark-text-primary">
                          {partner.name}
                        </h3>
                        <Badge
                          variant={PARTNER_COLORS[index % 5]}
                          size="sm"
                        >
                          {partner.share_percentage}% share
                        </Badge>
                      </div>
                    </div>
                    <motion.div
                      animate={{ rotate: expandedPartner === partner.id ? 180 : 0 }}
                      transition={springConfig}
                    >
                      <ChevronDown className="w-5 h-5 text-light-text-quaternary" />
                    </motion.div>
                  </div>

                  {/* Current Balances */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gradient-to-br from-blue/10 to-blue/5 rounded-xl p-3">
                      <div className="text-caption1 text-blue font-medium mb-1">USD Balance</div>
                      <div className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary">
                        {formatCurrency(partner.current_balance_usd, 'USD')}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-green/10 to-green/5 rounded-xl p-3">
                      <div className="text-caption1 text-green font-medium mb-1">PKR Balance</div>
                      <div className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary">
                        {formatCurrency(partner.current_balance_pkr, 'PKR')}
                      </div>
                    </div>
                  </div>

                  {/* Year Total */}
                  <div className="text-footnote text-light-text-secondary dark:text-dark-text-secondary">
                    <span className="font-medium">{selectedYear} earnings:</span>
                    <span className="text-green ml-2 font-semibold">{formatCurrency(partner.total_usd, 'USD')}</span>
                    <span className="mx-1 text-light-text-quaternary">+</span>
                    <span className="text-green font-semibold">{formatCurrency(partner.total_pkr, 'PKR')}</span>
                  </div>

                  {/* Expanded Details */}
                  <AnimatePresence>
                    {expandedPartner === partner.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={springConfig}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 pt-4 border-t border-black/[0.06] dark:border-white/[0.06]">
                          <h4 className="text-footnote font-semibold text-light-text-secondary dark:text-dark-text-secondary mb-3">
                            Monthly History
                          </h4>
                          {(distributionsByPartner[partner.id] || []).length === 0 ? (
                            <p className="text-footnote text-light-text-quaternary dark:text-dark-text-quaternary">
                              No distributions yet
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {(distributionsByPartner[partner.id] || []).slice(0, 6).map((dist) => (
                                <motion.div
                                  key={`${dist.year}-${dist.month}`}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  className="flex items-center justify-between text-footnote"
                                >
                                  <span className="text-light-text-secondary dark:text-dark-text-secondary">
                                    {dist.month} {dist.year}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-green font-medium">{formatCurrency(dist.income_usd, 'USD')}</span>
                                    <span className="text-light-text-quaternary">|</span>
                                    <span className="text-green font-medium">{formatCurrency(dist.income_pkr, 'PKR')}</span>
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          )}

                          {/* Bank Details */}
                          {partner.bank_name && (
                            <div className="mt-4 p-3 bg-gradient-to-br from-purple/5 to-pink/5 rounded-xl">
                              <div className="flex items-center gap-2 mb-2">
                                <Building2 className="w-4 h-4 text-purple" />
                                <h5 className="text-caption1 font-semibold text-light-text-tertiary dark:text-dark-text-tertiary">
                                  Bank Details
                                </h5>
                              </div>
                              <p className="text-footnote font-medium text-light-text-primary dark:text-dark-text-primary">
                                {partner.bank_name}
                              </p>
                              {partner.bank_account && (
                                <p className="text-caption1 text-light-text-secondary dark:text-dark-text-secondary">
                                  Account: {partner.bank_account}
                                </p>
                              )}
                              {partner.bank_iban && (
                                <p className="text-caption1 text-light-text-secondary dark:text-dark-text-secondary">
                                  IBAN: {partner.bank_iban}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </ContentCard>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Calculate Distributions Modal */}
      <Modal
        isOpen={isCalculateModalOpen}
        onClose={() => {
          setIsCalculateModalOpen(false)
          setCalculationResult(null)
        }}
        title="Calculate Monthly Distributions"
        size="lg"
      >
        <div className="space-y-6">
          {!calculationResult ? (
            <>
              <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
                Calculate profit distributions for all partners based on their share percentages.
                Make sure the pool balance for the selected month has been entered first.
              </p>

              {/* Month & Year Selection */}
              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="Month"
                  value={calcMonth}
                  onChange={setCalcMonth}
                  options={MONTHS.map(m => ({ value: m, label: m }))}
                />
                <Select
                  label="Year"
                  value={calcYear.toString()}
                  onChange={(value) => setCalcYear(parseInt(value))}
                  options={[
                    { value: '2024', label: '2024' },
                    { value: '2025', label: '2025' },
                    { value: '2026', label: '2026' }
                  ]}
                />
              </div>

              {/* Partner Preview */}
              <div className="p-4 bg-gradient-to-br from-purple/5 to-pink/5 rounded-2xl">
                <h4 className="text-subheadline font-semibold text-light-text-primary dark:text-dark-text-primary mb-4">
                  Partners to receive distribution
                </h4>
                <div className="space-y-3">
                  {partnerShares?.partners.filter(p => p.share_percentage > 0).map((partner, index) => (
                    <div key={partner.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br ${
                          index % 5 === 0 ? 'from-blue to-blue/70' :
                          index % 5 === 1 ? 'from-purple to-purple/70' :
                          index % 5 === 2 ? 'from-green to-green/70' :
                          index % 5 === 3 ? 'from-orange to-orange/70' :
                          'from-pink to-pink/70'
                        }`}>
                          <span className="text-white font-bold text-caption1">
                            {partner.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </span>
                        </div>
                        <span className="text-body text-light-text-secondary dark:text-dark-text-secondary">
                          {partner.name}
                        </span>
                      </div>
                      <Badge variant={PARTNER_COLORS[index % 5]} size="sm">
                        {partner.share_percentage}%
                      </Badge>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between">
                  <span className="text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary">
                    Total allocated
                  </span>
                  <span className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary">
                    {partnerShares?.totalShare}%
                  </span>
                </div>
              </div>

              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-4 bg-red/5 border border-red/20 rounded-2xl flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-red/10 flex items-center justify-center">
                      <Calculator className="w-4 h-4 text-red" />
                    </div>
                    <p className="text-red text-footnote">{error}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => setIsCalculateModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  fullWidth
                  icon={<Play className="w-4 h-4" />}
                  onClick={handleCalculateDistributions}
                  disabled={calculating}
                  isLoading={calculating}
                >
                  Calculate Now
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Success Result */}
              <div className="text-center mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", damping: 15, stiffness: 300 }}
                  className="w-20 h-20 bg-gradient-to-br from-green/20 to-green/10 rounded-2xl flex items-center justify-center mx-auto mb-4"
                >
                  <Check className="w-10 h-10 text-green" />
                </motion.div>
                <h3 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
                  Distributions Calculated!
                </h3>
                <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
                  {calculationResult.month} {calculationResult.year}
                </p>
              </div>

              {/* Profit Summary */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="p-5 bg-gradient-to-br from-blue/10 to-blue/5 rounded-2xl text-center"
                >
                  <div className="text-footnote text-blue font-medium mb-1">Total USD Profit</div>
                  <div className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary">
                    {formatCurrency(calculationResult.total_usd_profit, 'USD')}
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="p-5 bg-gradient-to-br from-green/10 to-green/5 rounded-2xl text-center"
                >
                  <div className="text-footnote text-green font-medium mb-1">Total PKR Profit</div>
                  <div className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary">
                    {formatCurrency(calculationResult.total_pkr_profit, 'PKR')}
                  </div>
                </motion.div>
              </div>

              {/* Distribution Breakdown */}
              <div className="p-5 bg-gradient-to-br from-purple/5 to-pink/5 rounded-2xl">
                <h4 className="text-subheadline font-semibold text-light-text-primary dark:text-dark-text-primary mb-4">
                  Distribution Breakdown
                </h4>
                <div className="space-y-4">
                  {calculationResult.distributions.map((dist, index) => (
                    <motion.div
                      key={dist.partner_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${
                          index % 5 === 0 ? 'from-blue to-blue/70' :
                          index % 5 === 1 ? 'from-purple to-purple/70' :
                          index % 5 === 2 ? 'from-green to-green/70' :
                          index % 5 === 3 ? 'from-orange to-orange/70' :
                          'from-pink to-pink/70'
                        }`}>
                          <span className="text-white font-bold text-caption1">
                            {dist.partner_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </span>
                        </div>
                        <div>
                          <div className="font-semibold text-body text-light-text-primary dark:text-dark-text-primary">
                            {dist.partner_name}
                          </div>
                          <div className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                            {dist.share_percentage}% share
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-body text-green">
                          {formatCurrency(dist.usd_share, 'USD')}
                        </div>
                        <div className="text-footnote text-green">
                          {formatCurrency(dist.pkr_share, 'PKR')}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <Button
                variant="primary"
                fullWidth
                onClick={() => {
                  setIsCalculateModalOpen(false)
                  setCalculationResult(null)
                }}
              >
                Done
              </Button>
            </>
          )}
        </div>
      </Modal>
    </motion.div>
  )
}
